import { decode, encode } from '@msgpack/msgpack'
import * as pako from 'pako'

// These functions are used to compress/decompress arbitrary objects.
// The process is object -> binary -> compressed binary -> base64
// It uses msgpack to encode/decode the binary data efficiently
// Then it sends this data to a web worker to do the compression and base64 conversion
// This isn't the fastest approach, but it prevents the UI thread from being blocked, so is good for time-insensitive operations

export async function compressData(data: any, encoding: 'deflate' | 'deflate-raw' | 'gzip'): Promise<string> {
  // Do MessagePack encoding on main thread (it's fast)
  const binaryData = encode(data)

  // Move compression and base64 conversion to worker (these are the heavy parts)
  return new Promise((resolve, reject) => {
    const workerCode = `
      self.onmessage = async function(e) {
        const { binaryData, encoding } = e.data;
        
        try {
          // Compress
          const cs = new CompressionStream(encoding);
          const writer = cs.writable.getWriter();
          writer.write(binaryData);
          writer.close();
          
          const compressedArray = await new Response(cs.readable).arrayBuffer();
          const compressedBytes = new Uint8Array(compressedArray);
          
          // Convert to base64 (this is the heavy part that can block UI)
          // Convert bytes directly to Latin1 string to avoid Unicode issues with btoa
          let binaryString = '';
          for (let i = 0; i < compressedBytes.length; i++) {
            binaryString += String.fromCharCode(compressedBytes[i]);
          }
          const base64 = btoa(binaryString);
          
          self.postMessage({ success: true, result: base64 });
        } catch (error) {
          self.postMessage({ success: false, error: error.message });
        }
      };
    `

    const blob = new Blob([workerCode], { type: 'application/javascript' })
    const blobUrl = URL.createObjectURL(blob)
    const worker = new Worker(blobUrl)

    const handleMessage = (e: MessageEvent) => {
      worker.terminate()
      URL.revokeObjectURL(blobUrl)

      if (e.data.success) {
        resolve(e.data.result)
      } else {
        reject(new Error(e.data.error))
      }
    }

    worker.addEventListener('message', handleMessage)
    worker.postMessage({ binaryData, encoding })
  })
}

export async function decompressData(base64: string, encoding: 'deflate' | 'deflate-raw' | 'gzip'): Promise<any> {
  // Move decompression and base64 decoding to worker
  return new Promise((resolve, reject) => {
    const workerCode = `
      self.onmessage = async function(e) {
        const { base64, encoding } = e.data;
        
        try {
          // Decode base64 (heavy operation)
          const binaryString = atob(base64);
          const compressedData = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            compressedData[i] = binaryString.charCodeAt(i);
          }
          
          // Decompress
          const ds = new DecompressionStream(encoding);
          const writer = ds.writable.getWriter();
          writer.write(compressedData);
          writer.close();
          
          const decompressedArray = await new Response(ds.readable).arrayBuffer();
          const binaryData = new Uint8Array(decompressedArray);
          
          self.postMessage({ success: true, result: binaryData });
        } catch (error) {
          self.postMessage({ success: false, error: error.message });
        }
      };
    `

    const blob = new Blob([workerCode], { type: 'application/javascript' })
    const blobUrl = URL.createObjectURL(blob)
    const worker = new Worker(blobUrl)

    const handleMessage = (e: MessageEvent) => {
      worker.terminate()
      URL.revokeObjectURL(blobUrl)

      if (e.data.success) {
        // Do MessagePack decoding on main thread (it's fast)
        const result = decode(e.data.result)
        resolve(result)
      } else {
        reject(new Error(e.data.error))
      }
    }

    worker.addEventListener('message', handleMessage)
    worker.postMessage({ base64, encoding })
  })
}

/**
 * Fully synchronous decompression - blocks the UI thread but gives immediate results
 * Use this when you need immediate access to data and can accept potential UI blocking
 * Good for smaller payloads or when speed is more important than UI responsiveness
 */
export function decompressDataSync(base64: string, encoding: 'deflate' | 'deflate-raw' | 'gzip'): any {
  // Decode base64 (synchronous, potentially blocking)
  const binaryString = atob(base64)
  const compressedData = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    compressedData[i] = binaryString.charCodeAt(i)
  }

  // Decompress synchronously using pako
  let decompressedData: Uint8Array

  if (encoding === 'deflate') {
    decompressedData = pako.inflate(compressedData)
  } else if (encoding === 'deflate-raw') {
    decompressedData = pako.inflateRaw(compressedData)
  } else if (encoding === 'gzip') {
    decompressedData = pako.ungzip(compressedData)
  } else {
    throw new Error(`Unsupported encoding: ${encoding}`)
  }

  // MessagePack decode (synchronous, fast)
  return decode(decompressedData)
}
